import React, { ReactNode, useCallback, useRef } from 'react';
import { ChevronDown, ChevronUp } from '@insurancemenu/imenu-components-lib/dist/icons';
import ValidationBlock from '@insurancemenu/imenu-components-lib/dist/components/ValidationBlock';
import { getValidationStylingType } from '@insurancemenu/imenu-components-lib/dist/helpers/getValidationStylingType';
import { ICustomizableElement, ITestingElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { SELECT_WITH_TAGS_ROLES } from './constants';
import TaggedSelectOption from './TaggedSelectOption';
import { TaggedSelectOptionType } from './types';
import { useTaggedSelectListbox } from './useTaggedSelectListbox';
import {
  DropdownPanelStyled,
  LabelStyled,
  TriggerIconStyled,
  OptionsListStyled,
  PlaceholderStyled,
  SelectedValueStyled,
  SelectedValueWrapperStyled,
  TaggedSelectWrapperStyled,
  TriggerStyled,
} from './styles';

// TO-DO: Add an ability to pass right-side content to SwissSelectOption & remove focus from disabled options in SwissSelect.
// Then delete this component.

export interface TaggedSelectProps extends ICustomizableElement, ITestingElement {
  value: string;
  onChange: (value: string) => void;
  options: TaggedSelectOptionType[];
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  validationPassed?: boolean;
  validationMessage?: ReactNode;
}

const TaggedSelect: React.FC<TaggedSelectProps> = ({
  value,
  onChange,
  options,
  id,
  label,
  placeholder = '',
  disabled = false,
  validationPassed = true,
  validationMessage,
  className,
  'data-test-id': dataTestId,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectId = id || 'effective-date-select-id';
  const isFieldDisabled = disabled || options.length < 1;
  const selectedOption = options.find((option) => option.value === value);
  const validation = getValidationStylingType(validationPassed, value || undefined);
  const hasValidationMessage = !validationPassed && (!value || Boolean(validationMessage));

  const updateSelection = useCallback(
    (optionValue: string): void => {
      const option = options.find((item) => item.value === optionValue);
      if (isFieldDisabled || option?.disabled) return;

      onChange(optionValue);
    },
    [isFieldDisabled, onChange, options],
  );

  const {
    isOpen,
    focusedIndex,
    triggerRef,
    registerOptionRef,
    closeListbox,
    focusTrigger,
    toggleListbox,
    handleTriggerKeyDown,
    handleOptionKeyDown,
  } = useTaggedSelectListbox({
    options,
    disabled: isFieldDisabled,
    selectedValue: value,
    wrapperRef,
    onSelectOption: updateSelection,
  });

  const selectOption = useCallback(
    (option: TaggedSelectOptionType): void => {
      if (option.disabled) return;

      updateSelection(option.value);
      closeListbox();
      focusTrigger();
    },
    [closeListbox, focusTrigger, updateSelection],
  );

  return (
    <TaggedSelectWrapperStyled ref={wrapperRef} className={className}>
      {label && <LabelStyled id={`${selectId}-label`}>{label}</LabelStyled>}

      <TriggerStyled
        ref={triggerRef}
        id={`${selectId}-trigger`}
        role="button"
        tabIndex={isFieldDisabled ? -1 : 0}
        $validation={validation}
        $disabled={isFieldDisabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${selectId}-listbox`}
        aria-labelledby={label ? `${selectId}-label` : undefined}
        aria-disabled={isFieldDisabled}
        data-test-id={dataTestId}
        data-role={SELECT_WITH_TAGS_ROLES.TRIGGER}
        onClick={toggleListbox}
        onKeyDown={handleTriggerKeyDown}
      >
        <SelectedValueWrapperStyled>
          {selectedOption ? (
            <SelectedValueStyled data-role={SELECT_WITH_TAGS_ROLES.SELECTED_VALUE} data-test-for={dataTestId}>
              {selectedOption.label}
            </SelectedValueStyled>
          ) : (
            <PlaceholderStyled data-role={SELECT_WITH_TAGS_ROLES.PLACEHOLDER} data-test-for={dataTestId}>
              {placeholder}
            </PlaceholderStyled>
          )}
        </SelectedValueWrapperStyled>
        <TriggerIconStyled aria-hidden="true">
          {isOpen ? <ChevronUp width="2.4rem" /> : <ChevronDown width="2.4rem" />}
        </TriggerIconStyled>
      </TriggerStyled>

      {isOpen && (
        <DropdownPanelStyled $hasValidationMessage={hasValidationMessage}>
          <OptionsListStyled
            id={`${selectId}-listbox`}
            role="listbox"
            data-role={SELECT_WITH_TAGS_ROLES.LISTBOX}
            data-test-for={dataTestId}
            aria-labelledby={label ? `${selectId}-label` : undefined}
          >
            {options.map((option, index) => (
              <TaggedSelectOption
                key={option.value}
                option={option}
                isSelected={option.value === value}
                isFocused={index === focusedIndex}
                optionRef={registerOptionRef(index)}
                data-test-id={dataTestId}
                onSelect={(): void => selectOption(option)}
                onKeyDown={(event): void => handleOptionKeyDown(event, index, option.value)}
              />
            ))}
          </OptionsListStyled>
        </DropdownPanelStyled>
      )}

      <ValidationBlock
        id={`${selectId}-validation`}
        data-test-id={dataTestId}
        value={value || undefined}
        validationMessage={validationMessage}
        validationPassed={validationPassed}
      />
    </TaggedSelectWrapperStyled>
  );
};

export default TaggedSelect;
